// Minimal ISO-BMFF reading for progressive playback of FRAGMENTED MP4 (init = ftyp+moov with mvex; media =
// moof+mdat pairs). Only what Media Source Extensions needs: top-level box headers, codec strings for the sample
// entries browsers commonly decode (AVC, AAC/MP3, Opus, VP9), track timescales and each fragment's start time.
// Anything else (HEVC, AV1, Dolby, encrypted entries, non-fragmented files) is reported as unsupported.

const td = new TextDecoder('latin1');
const u32 = (b, o) => ((b[o] << 24) >>> 0) + (b[o + 1] << 16) + (b[o + 2] << 8) + b[o + 3];
const u64 = (b, o) => u32(b, o) * 2 ** 32 + u32(b, o + 4);
const type4 = (b, o) => td.decode(b.subarray(o, o + 4));
const hex2 = (n) => n.toString(16).toUpperCase().padStart(2, '0');
const dec2 = (n) => String(n).padStart(2, '0');

/**
 * Header of the box at `off` inside `bytes` (which holds at least 16 bytes from `off` when available).
 * `fileSize` resolves size 0 ("to end of file").
 * @returns {{ type: string, size: number, header: number } | null} null when the header itself is incomplete
 */
export function boxHeader(bytes, off, fileSize) {
  if (off + 8 > bytes.length) return null;
  let size = u32(bytes, off), header = 8;
  const type = type4(bytes, off + 4);
  if (size === 1) { if (off + 16 > bytes.length) return null; size = u64(bytes, off + 8); header = 16; }
  else if (size === 0) size = fileSize - off;
  if (!Number.isSafeInteger(size) || size < header) throw new Error('mp4: bad box size');
  return { type, size, header };
}

/** Child boxes of a container payload `[start, end)`. */
function children(b, start, end) {
  const out = [];
  for (let o = start; o + 8 <= end;) {
    const h = boxHeader(b, o, end);
    if (!h || o + h.size > end) throw new Error('mp4: truncated box');
    out.push({ type: h.type, start: o, body: o + h.header, end: o + h.size });
    o += h.size;
  }
  return out;
}
const child = (b, box, type) => children(b, box.body, box.end).find((c) => c.type === type) || null;

function codecOfEntry(b, e, handler) {
  const t = e.type;
  // VisualSampleEntry children start 78 bytes into the entry body; AudioSampleEntry (v0) 28 bytes.
  const kids = (skip) => children(b, e.body + skip, e.end);
  if (handler === 'vide') {
    const k = kids(78);
    if (t === 'avc1' || t === 'avc3') {
      const c = k.find((x) => x.type === 'avcC');
      if (!c || c.end - c.body < 4) return null;
      return `${t}.${hex2(b[c.body + 1])}${hex2(b[c.body + 2])}${hex2(b[c.body + 3])}`;
    }
    if (t === 'vp09') {
      const c = k.find((x) => x.type === 'vpcC');
      if (!c || c.end - c.body < 7) return null;
      return `vp09.${dec2(b[c.body + 4])}.${dec2(b[c.body + 5])}.${dec2(b[c.body + 6] >> 4)}`;
    }
    return null;
  }
  if (handler === 'soun') {
    if (t === 'Opus') return 'opus';
    if (t !== 'mp4a') return null;
    const c = kids(28).find((x) => x.type === 'esds');
    if (!c) return null;
    // ES_Descriptor (0x03) → DecoderConfigDescriptor (0x04): objectTypeIndication; DecoderSpecificInfo (0x05): AOT.
    let o = c.body + 4; // full box version/flags
    const desc = () => { const tag = b[o++]; let len = 0; for (let i = 0; i < 4; i++) { const x = b[o++]; len = (len << 7) | (x & 0x7f); if (!(x & 0x80)) break; } return { tag, len }; };
    let d = desc(); if (d.tag !== 3) return null;
    const flags = b[o + 2]; o += 3;
    if (flags & 0x80) o += 2; if (flags & 0x40) o += 1 + b[o]; if (flags & 0x20) o += 2;
    d = desc(); if (d.tag !== 4) return null;
    const oti = b[o]; o += 13;
    if (oti === 0x6b || oti === 0x69) return 'mp3';
    if (oti !== 0x40) return null;
    d = desc(); if (d.tag !== 5 || d.len < 1) return 'mp4a.40.2';
    let aot = b[o] >> 3;
    if (aot === 31 && d.len >= 2) aot = 32 + (((b[o] & 7) << 3) | (b[o + 1] >> 5));
    return `mp4a.40.${aot}`;
  }
  return null;
}

/**
 * Parse an init segment (ftyp+moov bytes).
 * @returns {{ fragmented: boolean, tracks: {id: number, timescale: number, handler: string, codec: string|null}[], mime: string|null }}
 */
export function parseInit(b) {
  const top = children(b, 0, b.length);
  const moov = top.find((x) => x.type === 'moov');
  if (!moov) throw new Error('mp4: no moov');
  const fragmented = !!child(b, moov, 'mvex');
  const tracks = [];
  for (const trak of children(b, moov.body, moov.end).filter((x) => x.type === 'trak')) {
    const tkhd = child(b, trak, 'tkhd'), mdia = child(b, trak, 'mdia');
    if (!tkhd || !mdia) continue;
    const id = u32(b, tkhd.body + (b[tkhd.body] === 1 ? 20 : 12));
    const mdhd = child(b, mdia, 'mdhd'), hdlr = child(b, mdia, 'hdlr');
    const timescale = mdhd ? u32(b, mdhd.body + (b[mdhd.body] === 1 ? 20 : 12)) : 0;
    const handler = hdlr ? type4(b, hdlr.body + 8) : '';
    const stsd = child(b, child(b, child(b, mdia, 'minf') || mdia, 'stbl') || mdia, 'stsd');
    const entries = stsd ? children(b, stsd.body + 8, stsd.end) : [];
    tracks.push({ id, timescale, handler, codec: entries.length === 1 ? codecOfEntry(b, entries[0], handler) : null });
  }
  const av = tracks.filter((t) => t.handler === 'vide' || t.handler === 'soun');
  const ok = fragmented && av.length > 0 && av.every((t) => t.codec && t.timescale > 0);
  const kind = av.some((t) => t.handler === 'vide') ? 'video' : 'audio';
  return { fragmented, tracks, mime: ok ? `${kind}/mp4; codecs="${av.map((t) => t.codec).join(', ')}"` : null };
}

/** Start time (seconds) of a fragment from its moof: first traf's tfdt baseMediaDecodeTime / track timescale. */
export function moofTime(b, timescales) {
  const moof = children(b, 0, b.length).find((x) => x.type === 'moof');
  if (!moof) return null;
  for (const traf of children(b, moof.body, moof.end).filter((x) => x.type === 'traf')) {
    const tfhd = child(b, traf, 'tfhd'), tfdt = child(b, traf, 'tfdt');
    if (!tfhd || !tfdt) continue;
    const scale = timescales.get(u32(b, tfhd.body + 4));
    if (!scale) continue;
    const t = b[tfdt.body] === 1 ? u64(b, tfdt.body + 4) : u32(b, tfdt.body + 4);
    return t / scale;
  }
  return null;
}
